import { BASE_URL } from './constants.js';
const absolutize = (u) => (u && u.startsWith('/')) ? `${BASE_URL}${u}` : u;

const organization = {
  '@type': 'Organization',
  name: 'HermesReads',
  url: `${BASE_URL}/`,
  logo: {
    '@type': 'ImageObject',
    url: `${BASE_URL}/images/logo.png`
  }
};

const templates = {
  home: {
    en: {
      htmlLang: 'en',
      title: 'HermesReads | Digital Library, EPUB Conversion, Metadata, and Publishing Services',
      description: 'HermesReads is a digital library, publishing project, and book-production service offering public-domain books, revived editions, EPUB conversion, metadata enrichment, ONIX preparation, and backlist recovery.',
      keywords: 'HermesReads, digital library, public domain books, EPUB conversion, ebook conversion, metadata services, ONIX metadata, book production services, publishing services, backlist recovery, ebook formatting, print on demand preparation',
      canonical: `${BASE_URL}/`,
      og: {
        title: 'HermesReads – Free Public Domain Ebooks',
        description: 'HermesReads combines a public digital library with publishing support, EPUB conversion, metadata enrichment, ONIX preparation, revived editions, and backlist recovery.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'HermesReads – Public Domain Library',
        description: 'A digital library and book-production project offering public-domain books, EPUB conversion, metadata enrichment, ONIX preparation, and publishing support.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'HermesReads',
        'url': `${BASE_URL}/`,
        'inLanguage': 'en',
        'publisher': organization,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/search?query={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      variants: {
        concise: {
          title: 'HermesReads | Free Ebooks',
          description: 'Read free classic books on HermesReads.'
        },
        bold: {
          title: 'HermesReads: Classic Ebooks Free!',
          description: 'Discover free public domain books now!'
        },
        curiosity: {
          title: 'Why Read Classics? | HermesReads',
          description: 'What makes our free ebooks special? Find out!'
        }
      }
    },
    de: {
      htmlLang: 'de',
      title: 'HermesReads – Kostenlose gemeinfreie E-Books',
      description: 'Durchstöbere Tausende kostenloser gemeinfreier Bücher in Geschichte, Philosophie und Wissenschaft.',
      keywords: 'kostenlose ebooks, gemeinfrei, klassische literatur, philosophie bücher, geschichte bücher, HermesReads',
      canonical: `${BASE_URL}/`,
      og: {
        title: 'HermesReads – Kostenlose E-Books',
        description: 'Lies klassische gemeinfreie Texte kostenlos auf HermesReads.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'HermesReads – Gemeinfreie Bibliothek',
        description: 'Entdecke kostenlose gemeinfreie E-Books auf HermesReads.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'HermesReads',
        'url': `${BASE_URL}/`,
        'inLanguage': 'de',
        'publisher': organization,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/search?query={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      variants: {
        concise: {
          title: 'HermesReads | Kostenlose E-Books',
          description: 'Lies kostenlose Klassiker auf HermesReads.'
        },
        bold: {
          title: 'HermesReads: Kostenlose Klassiker!',
          description: 'Entdecke jetzt gemeinfreie Bücher!'
        },
        curiosity: {
          title: 'Warum Klassiker lesen? | HermesReads',
          description: 'Was macht unsere E-Books besonders? Finde es heraus!'
        }
      }
    },
    fr: {
      htmlLang: 'fr',
      title: 'HermesReads – Ebooks gratuits du domaine public',
      description: 'Explorez des milliers d’ebooks gratuits du domaine public en histoire, philosophie et science.',
      keywords: 'ebooks gratuits, domaine public, littérature classique, livres de philosophie, livres d’histoire, HermesReads',
      canonical: `${BASE_URL}/`,
      og: {
        title: 'HermesReads – Ebooks Gratuits',
        description: 'Lisez des textes classiques du domaine public gratuitement.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'HermesReads – Bibliothèque du Domaine Public',
        description: 'Découvrez des ebooks gratuits du domaine public.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'HermesReads',
        'url': `${BASE_URL}/`,
        'inLanguage': 'fr',
        'publisher': organization,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/search?query={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      variants: {
        concise: {
          title: 'HermesReads | Ebooks Gratuits',
          description: 'Lisez des livres classiques gratuits.'
        },
        bold: {
          title: 'HermesReads : Ebooks Classiques Gratuits !',
          description: 'Découvrez des livres gratuits maintenant !'
        },
        curiosity: {
          title: 'Pourquoi lire des classiques ? | HermesReads',
          description: 'Qu’est-ce qui rend nos ebooks spéciaux ?'
        }
      }
    },
    sv: {
      htmlLang: 'sv',
      title: 'HermesReads – Gratis e-böcker i public domain',
      description: 'Bläddra bland tusentals gratis e-böcker i historia, filosofi och vetenskap på HermesReads.',
      keywords: 'gratis e-böcker, public domain, klassisk litteratur, filosofiböcker, historieböcker, HermesReads',
      canonical: `${BASE_URL}/`,
      og: {
        title: 'HermesReads – Gratis E-böcker',
        description: 'Läs klassiska public domain-texter gratis på HermesReads.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'HermesReads – Public Domain Bibliotek',
        description: 'Upptäck gratis e-böcker i public domain.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'HermesReads',
        'url': `${BASE_URL}/`,
        'inLanguage': 'sv',
        'publisher': organization,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/search?query={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      variants: {
        concise: {
          title: 'HermesReads | Gratis E-böcker',
          description: 'Läs gratis klassiska böcker.'
        },
        bold: {
          title: 'HermesReads: Gratis Klassiker!',
          description: 'Upptäck gratis böcker nu!'
        },
        curiosity: {
          title: 'Varför läsa klassiker? | HermesReads',
          description: 'Vad gör våra e-böcker speciella?'
        }
      }
    },
    es: {
      htmlLang: 'es',
      title: 'HermesReads – Ebooks gratuitos de dominio público',
      description: 'Explora miles de ebooks gratuitos de dominio público en historia, filosofía y ciencia.',
      keywords: 'ebooks gratuitos, dominio público, literatura clásica, libros de filosofía, libros de historia, HermesReads',
      canonical: `${BASE_URL}/`,
      og: {
        title: 'HermesReads – Ebooks Gratuitos',
        description: 'Lee textos clásicos de dominio público gratis.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'HermesReads – Biblioteca de Dominio Público',
        description: 'Descubre ebooks gratuitos de dominio público.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'HermesReads',
        'url': `${BASE_URL}/`,
        'inLanguage': 'es',
        'publisher': organization,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/search?query={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      variants: {
        concise: {
          title: 'HermesReads | Ebooks Gratuitos',
          description: 'Lee libros clásicos gratis.'
        },
        bold: {
          title: 'HermesReads: ¡Ebooks Clásicos Gratis!',
          description: '¡Descubre libros gratuitos ahora!'
        },
        curiosity: {
          title: '¿Por qué leer clásicos? | HermesReads',
          description: '¿Qué hace especiales a nuestros ebooks?'
        }
      }
    },
    fi: {
      htmlLang: 'fi',
      title: 'HermesReads – Ilmaisia public domain -e-kirjoja',
      description: 'Selaa tuhansia ilmaisia public domain -kirjoja historiassa, filosofiassa ja tieteessä HermesReads-sivustolla.',
      keywords: 'ilmaisia e-kirjoja, public domain, klassinen kirjallisuus, filosofiakirjat, historiakirjat, HermesReads',
      canonical: `${BASE_URL}/`,
      og: {
        title: 'HermesReads – Ilmaisia e-kirjoja',
        description: 'Lue klassisia public domain -tekstejä ilmaiseksi HermesReads-sivustolla.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'HermesReads – Public Domain -kirjasto',
        description: 'Tutustu ilmaisiin public domain -e-kirjoihin HermesReads-sivustolla.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'HermesReads',
        'url': `${BASE_URL}/`,
        'inLanguage': 'fi',
        'publisher': organization,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/search?query={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      variants: {
        concise: {
          title: 'HermesReads | Ilmaisia e-kirjoja',
          description: 'Lue ilmaisia klassikkokirjoja.'
        },
        bold: {
          title: 'HermesReads: Ilmaisia klassikoita!',
          description: 'Tutustu ilmaisiin kirjoihin nyt!'
        },
        curiosity: {
          title: 'Miksi lukea klassikoita? | HermesReads',
          description: 'Mikä tekee e-kirjoistamme erityisiä?'
        }
      }
    },
    pt: {
      htmlLang: 'pt',
      title: 'HermesReads – E-books gratuitos de domínio público',
      description: 'Explore milhares de e-books gratuitos de domínio público em história, filosofia e ciência.',
      keywords: 'ebooks gratuitos, domínio público, literatura clássica, livros de filosofia, livros de história, HermesReads',
      canonical: `${BASE_URL}/`,
      og: {
        title: 'HermesReads – E-books Gratuitos',
        description: 'Leia textos clássicos de domínio público gratuitamente.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'HermesReads – Biblioteca de Domínio Público',
        description: 'Descubra e-books gratuitos de domínio público.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'HermesReads',
        'url': `${BASE_URL}/`,
        'inLanguage': 'pt',
        'publisher': organization,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/search?query={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      variants: {
        concise: {
          title: 'HermesReads | E-books Gratuitos',
          description: 'Leia livros clássicos grátis.'
        },
        bold: {
          title: 'HermesReads: E-books Clássicos Grátis!',
          description: 'Descubra livros gratuitos agora!'
        },
        curiosity: {
          title: 'Por que ler clássicos? | HermesReads',
          description: 'O que torna nossos e-books especiais?'
        }
      }
    }
  },
  about: {
    en: {
      htmlLang: 'en',
      title: 'About HermesReads | Library, Publishing Infrastructure, EPUB Conversion, and Metadata Work',
      description: 'HermesReads is a digital library and publishing infrastructure project focused on public-domain books, revived editions, EPUB conversion, metadata enrichment, catalog cleanup, translation projects, ONIX preparation, and book-production workflows.',
      keywords: 'about HermesReads, digital library, publishing infrastructure, EPUB conversion, metadata enrichment, ONIX preparation, book production, public domain books, backlist recovery, catalog cleanup, translation projects, ebook publishing',
      canonical: `${BASE_URL}/about`,
      og: {
        title: 'About HermesReads – Free Public Domain Library',
        description: 'HermesReads combines a public digital library with publishing infrastructure, EPUB conversion, metadata enrichment, catalog cleanup, translation projects, and book-production workflows.',
        type: 'website',
        image: '/images/about-banner.jpg',
        url: `${BASE_URL}/about`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads about page banner'
      },
      twitter: {
        title: 'About HermesReads | Free Public Domain Library',
        description: 'Our mission to provide free access to classic texts worldwide.',
        card: 'summary_large_image',
        image: '/images/about-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads about banner'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'About HermesReads',
        'url': `${BASE_URL}/about`,
        'inLanguage': 'en',
        'publisher': organization
      },
      variants: {
        concise: {
          title: 'About HermesReads',
          description: 'Our mission for free classic texts.'
        },
        bold: {
          title: 'HermesReads: Our Mission!',
          description: 'Free classic texts for all!'
        },
        curiosity: {
          title: 'What is HermesReads? | About',
          description: 'Why do we share free books?'
        }
      }
    },
    de: {
      htmlLang: 'de',
      title: 'Über HermesReads',
      description: 'Erfahre mehr über unsere Mission, Sachbücher und Klassiker weltweit frei zugänglich zu machen.',
      keywords: 'über HermesReads, kostenlose ebooks, gemeinfreie mission, offene bibliothek, klassische literatur',
      canonical: `${BASE_URL}/about`,
      og: {
        title: 'Über HermesReads',
        description: 'Entdecke unsere Mission, kostenlose Klassiker weltweit zu teilen.',
        type: 'website',
        image: '/images/about-banner.jpg',
        url: `${BASE_URL}/about`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Über HermesReads',
        description: 'Unsere Mission, freien Zugang zu Klassikern zu bieten.',
        card: 'summary_large_image',
        image: '/images/about-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'Über HermesReads',
        'url': `${BASE_URL}/about`,
        'inLanguage': 'de',
        'publisher': organization
      },
      variants: {
        concise: {
          title: 'Über HermesReads',
          description: 'Unsere Mission für freie Klassiker.'
        },
        bold: {
          title: 'HermesReads: Unsere Mission!',
          description: 'Kostenlose Klassiker für alle!'
        },
        curiosity: {
          title: 'Was ist HermesReads? | Über',
          description: 'Warum teilen wir freie Bücher?'
        }
      }
    },
    fr: {
      htmlLang: 'fr',
      title: 'À propos de HermesReads',
      description: 'Découvrez notre mission de rendre les textes non-fictionnels et classiques accessibles gratuitement.',
      keywords: 'à propos de HermesReads, ebooks gratuits, mission domaine public, bibliothèque ouverte, littérature classique',
      canonical: `${BASE_URL}/about`,
      og: {
        title: 'À propos de HermesReads',
        description: 'Découvrez notre mission de partager des textes classiques gratuitement.',
        type: 'website',
        image: '/images/about-banner.jpg',
        url: `${BASE_URL}/about`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'À propos de HermesReads',
        description: 'Notre mission d’accès gratuit aux textes classiques.',
        card: 'summary_large_image',
        image: '/images/about-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'À propos de HermesReads',
        'url': `${BASE_URL}/about`,
        'inLanguage': 'fr',
        'publisher': organization
      },
      variants: {
        concise: {
          title: 'À propos de HermesReads',
          description: 'Notre mission pour des textes gratuits.'
        },
        bold: {
          title: 'HermesReads : Notre Mission !',
          description: 'Textes classiques gratuits pour tous !'
        },
        curiosity: {
          title: 'Qu’est-ce que HermesReads ? | À propos',
          description: 'Pourquoi partageons-nous des livres gratuits ?'
        }
      }
    },
    sv: {
      htmlLang: 'sv',
      title: 'Om HermesReads',
      description: 'Lär dig om vårt uppdrag att göra sakprosa och klassiska texter fritt tillgängliga världen över.',
      keywords: 'om HermesReads, gratis e-böcker, public domain uppdrag, öppet bibliotek, klassisk litteratur',
      canonical: `${BASE_URL}/about`,
      og: {
        title: 'Om HermesReads',
        description: 'Upptäck vårt uppdrag att dela gratis klassiska texter globalt.',
        type: 'website',
        image: '/images/about-banner.jpg',
        url: `${BASE_URL}/about`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Om HermesReads',
        description: 'Vårt uppdrag att ge fri tillgång till klassiska texter.',
        card: 'summary_large_image',
        image: '/images/about-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'Om HermesReads',
        'url': `${BASE_URL}/about`,
        'inLanguage': 'sv',
        'publisher': organization
      },
      variants: {
        concise: {
          title: 'Om HermesReads',
          description: 'Vårt uppdrag för gratis klassiker.'
        },
        bold: {
          title: 'HermesReads: Vårt Uppdrag!',
          description: 'Gratis klassiska texter för alla!'
        },
        curiosity: {
          title: 'Vad är HermesReads? | Om',
          description: 'Varför delar vi gratis böcker?'
        }
      }
    },
    es: {
      htmlLang: 'es',
      title: 'Acerca de HermesReads',
      description: 'Conoce nuestra misión de hacer textos no ficticios y clásicos accesibles gratuitamente en todo el mundo.',
      keywords: 'acerca de HermesReads, ebooks gratuitos, misión dominio público, biblioteca abierta, literatura clásica',
      canonical: `${BASE_URL}/about`,
      og: {
        title: 'Acerca de HermesReads',
        description: 'Descubre nuestra misión de compartir textos clásicos gratis globalmente.',
        type: 'website',
        image: '/images/about-banner.jpg',
        url: `${BASE_URL}/about`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Acerca de HermesReads',
        description: 'Nuestra misión de acceso gratuito a textos clásicos.',
        card: 'summary_large_image',
        image: '/images/about-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'Acerca de HermesReads',
        'url': `${BASE_URL}/about`,
        'inLanguage': 'es',
        'publisher': organization
      },
      variants: {
        concise: {
          title: 'Acerca de HermesReads',
          description: 'Nuestra misión para textos clásicos gratis.'
        },
        bold: {
          title: 'HermesReads: ¡Nuestra Misión!',
          description: '¡Textos clásicos gratis para todos!'
        },
        curiosity: {
          title: '¿Qué es HermesReads? | Acerca',
          description: '¿Por qué compartimos libros gratis?'
        }
      }
    },
    fi: {
      htmlLang: 'fi',
      title: 'Tietoa HermesReadsistä',
      description: 'Tutustu missioomme tehdä tietokirjoja ja klassisia tekstejä vapaasti saataville maailmanlaajuisesti.',
      keywords: 'tietoa HermesReadsistä, ilmaisia e-kirjoja, public domain -missio, avoin kirjasto, klassinen kirjallisuus',
      canonical: `${BASE_URL}/about`,
      og: {
        title: 'Tietoa HermesReadsistä',
        description: 'Tutustu missioomme jakaa ilmaisia klassisia tekstejä maailmanlaajuisesti.',
        type: 'website',
        image: '/images/about-banner.jpg',
        url: `${BASE_URL}/about`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Tietoa HermesReadsistä',
        description: 'Missiomme tarjota ilmainen pääsy klassisiin teksteihin.',
        card: 'summary_large_image',
        image: '/images/about-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'Tietoa HermesReadsistä',
        'url': `${BASE_URL}/about`,
        'inLanguage': 'fi',
        'publisher': organization
      },
      variants: {
        concise: {
          title: 'Tietoa HermesReadsistä',
          description: 'Missiomme ilmaisiin klassisiin teksteihin.'
        },
        bold: {
          title: 'HermesReads: Missiomme!',
          description: 'Ilmaisia klassisia tekstejä kaikille!'
        },
        curiosity: {
          title: 'Mikä on HermesReads? | Tietoa',
          description: 'Miksi jaamme ilmaisia kirjoja?'
        }
      }
    },
    pt: {
      htmlLang: 'pt',
      title: 'Sobre a HermesReads',
      description: 'Conheça nossa missão de tornar textos não ficcionais e clássicos acessíveis gratuitamente em todo o mundo.',
      keywords: 'sobre a HermesReads, ebooks gratuitos, missão de domínio público, biblioteca aberta, literatura clássica',
      canonical: `${BASE_URL}/about`,
      og: {
        title: 'Sobre a HermesReads',
        description: 'Descubra nossa missão de compartilhar textos clássicos gratuitamente em todo o mundo.',
        type: 'website',
        image: '/images/about-banner.jpg',
        url: `${BASE_URL}/about`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Sobre a HermesReads',
        description: 'Nossa missão de oferecer acesso gratuito a textos clássicos.',
        card: 'summary_large_image',
        image: '/images/about-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'Sobre a HermesReads',
        'url': `${BASE_URL}/about`,
        'inLanguage': 'pt',
        'publisher': organization
      },
      variants: {
        concise: {
          title: 'Sobre a HermesReads',
          description: 'Nossa missão para textos clássicos gratuitos.'
        },
        bold: {
          title: 'HermesReads: Nossa Missão!',
          description: 'Textos clássicos gratuitos para todos!'
        },
        curiosity: {
          title: 'O que é HermesReads? | Sobre',
          description: 'Por que compartilhamos livros grátis?'
        }
      }
    }
  },
  contact: {
    en: {
      htmlLang: 'en',
      title: 'Contact HermesReads | EPUB Conversion, Metadata, Backlist Recovery, and Publishing Support',
      description: 'Contact HermesReads for EPUB conversion, metadata enrichment, ONIX preparation, OCR cleanup, publisher backlist recovery, public-domain edition work, archive projects, and book-production workflows.',
      keywords: 'contact HermesReads, EPUB conversion help, metadata services, ONIX preparation, backlist recovery, publisher catalog cleanup, book production help, ebook formatting, OCR cleanup, public domain publishing',
      canonical: `${BASE_URL}/contact`,
      og: {
        title: 'Contact HermesReads | EPUB Conversion, Metadata, and Publishing Support',
        description: 'Get in touch about EPUB conversion, metadata enrichment, ONIX preparation, OCR cleanup, backlist recovery, archive projects, and book-production workflows.',
        type: 'website',
        image: '/images/contact-banner.jpg',
        url: `${BASE_URL}/contact`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads contact page'
      },
      twitter: {
        title: 'Contact HermesReads Support',
        description: 'Get in touch with our free ebook library team.',
        card: 'summary_large_image',
        image: '/images/contact-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads contact banner'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Contact Us',
        'url': `${BASE_URL}/contact`,
        'inLanguage': 'en',
        'publisher': organization
      }
    },
    de: {
      htmlLang: 'de',
      title: 'Kontakt | HermesReads',
      description: 'Kontaktiere HermesReads für Anfragen, Feedback oder Kooperationen.',
      keywords: 'kontakt HermesReads, kostenlose ebook unterstützung, gemeinfreie anfragen, offene bibliothek kontakt',
      canonical: `${BASE_URL}/contact`,
      og: {
        title: 'Kontakt | HermesReads',
        description: 'Erreiche uns für Anfragen oder Feedback zu unseren kostenlosen E-Books.',
        type: 'website',
        image: '/images/contact-banner.jpg',
        url: `${BASE_URL}/contact`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Kontakt HermesReads',
        description: 'Kontaktiere unsere kostenlose E-Book-Bibliothek.',
        card: 'summary_large_image',
        image: '/images/contact-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Kontakt',
        'url': `${BASE_URL}/contact`,
        'inLanguage': 'de',
        'publisher': organization
      }
    },
    fr: {
      htmlLang: 'fr',
      title: 'Contactez-nous | HermesReads',
      description: 'Contactez HermesReads pour des questions, commentaires ou collaborations.',
      keywords: 'contactez HermesReads, support ebooks gratuits, demandes domaine public, contact bibliothèque ouverte',
      canonical: `${BASE_URL}/contact`,
      og: {
        title: 'Contactez-nous | HermesReads',
        description: 'Contactez-nous pour des questions ou commentaires sur nos ebooks gratuits.',
        type: 'website',
        image: '/images/contact-banner.jpg',
        url: `${BASE_URL}/contact`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Contactez HermesReads',
        description: 'Contactez notre bibliothèque d’ebooks gratuits.',
        card: 'summary_large_image',
        image: '/images/contact-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Contactez-nous',
        'url': `${BASE_URL}/contact`,
        'inLanguage': 'fr',
        'publisher': organization
      }
    },
    sv: {
      htmlLang: 'sv',
      title: 'Kontakta Oss | HermesReads',
      description: 'Kontakta HermesReads för frågor, feedback eller samarbeten.',
      keywords: 'kontakta HermesReads, gratis e-bok support, public domain frågor, öppet bibliotek kontakt',
      canonical: `${BASE_URL}/contact`,
      og: {
        title: 'Kontakta Oss | HermesReads',
        description: 'Nå oss för frågor eller feedback om våra gratis e-böcker.',
        type: 'website',
        image: '/images/contact-banner.jpg',
        url: `${BASE_URL}/contact`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Kontakta HermesReads',
        description: 'Kontakta vårt gratis e-bok bibliotek.',
        card: 'summary_large_image',
        image: '/images/contact-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Kontakta Oss',
        'url': `${BASE_URL}/contact`,
        'inLanguage': 'sv',
        'publisher': organization
      }
    },
    es: {
      htmlLang: 'es',
      title: 'Contáctanos | HermesReads',
      description: 'Contacta con HermesReads para consultas, comentarios o colaboraciones.',
      keywords: 'contacta HermesReads, soporte ebooks gratuitos, consultas dominio público, contacto biblioteca abierta',
      canonical: `${BASE_URL}/contact`,
      og: {
        title: 'Contáctanos | HermesReads',
        description: 'Contacta para consultas o comentarios sobre nuestros ebooks gratuitos.',
        type: 'website',
        image: '/images/contact-banner.jpg',
        url: `${BASE_URL}/contact`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Contáctanos HermesReads',
        description: 'Contacta con nuestra biblioteca de ebooks gratuitos.',
        card: 'summary_large_image',
        image: '/images/contact-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Contáctanos',
        'url': `${BASE_URL}/contact`,
        'inLanguage': 'es',
        'publisher': organization
      }
    },
    fi: {
      htmlLang: 'fi',
      title: 'Ota Yhteyttä | HermesReads',
      description: 'Ota yhteyttä HermesReadsiin kysymyksissä, palautteessa tai yhteistyöasioissa.',
      keywords: 'ota yhteyttä HermesReadsiin, ilmaisten e-kirjojen tuki, public domain -kysymykset, avoimen kirjaston yhteystiedot',
      canonical: `${BASE_URL}/contact`,
      og: {
        title: 'Ota Yhteyttä | HermesReads',
        description: 'Ota yhteyttä kysymyksissä tai palautteessa liittyen ilmaisiin e-kirjoihimme.',
        type: 'website',
        image: '/images/contact-banner.jpg',
        url: `${BASE_URL}/contact`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Ota Yhteyttä HermesReads',
        description: 'Ota yhteyttä ilmaiseen e-kirjastoomme.',
        card: 'summary_large_image',
        image: '/images/contact-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Ota Yhteyttä',
        'url': `${BASE_URL}/contact`,
        'inLanguage': 'fi',
        'publisher': organization
      }
    },
    pt: {
      htmlLang: 'pt',
      title: 'Contate-nos | HermesReads',
      description: 'Entre em contato com a HermesReads para perguntas, feedback ou colaborações.',
      keywords: 'contate HermesReads, suporte a ebooks gratuitos, perguntas sobre domínio público, contato com biblioteca aberta',
      canonical: `${BASE_URL}/contact`,
      og: {
        title: 'Contate-nos | HermesReads',
        description: 'Entre em contato para perguntas ou feedback sobre nossos ebooks gratuitos.',
        type: 'website',
        image: '/images/contact-banner.jpg',
        url: `${BASE_URL}/contact`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Contate HermesReads',
        description: 'Entre em contato com nossa biblioteca de ebooks gratuitos.',
        card: 'summary_large_image',
        image: '/images/contact-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Contate-nos',
        'url': `${BASE_URL}/contact`,
        'inLanguage': 'pt',
        'publisher': organization
      }
    }
  },
  services: {
    en: {
      htmlLang: 'en',
      title: 'EPUB Conversion, Metadata Services, ONIX, and Backlist Recovery | HermesReads',
      description: 'HermesReads helps publishers, authors, archives, and catalog owners convert old books, manuscripts, scans, PDFs, OCR text, and existing files into clean EPUBs, print-ready bodies, enriched metadata, ONIX-ready records, and distributor-ready publishing material.',
      keywords: 'EPUB conversion, ebook conversion, ebook formatting, metadata services, ONIX metadata, book metadata enrichment, backlist recovery, publisher backlist digitization, OCR cleanup, PDF to EPUB, print to EPUB, PublishDrive preparation, KDP formatting, IngramSpark preparation, public domain publishing',
      canonical: `${BASE_URL}/services`,
      og: {
        title: 'EPUB Conversion, Metadata Services, and Backlist Recovery | HermesReads',
        description: 'Convert older books, PDFs, scans, OCR text, DOCX files, and catalog material into clean EPUBs, stronger metadata, ONIX-ready records, and distributor-ready publishing packages.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/services`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads publishing and book-production services'
      },
      twitter: {
        title: 'EPUB Conversion, Metadata Services, and Backlist Recovery | HermesReads',
        description: 'Book-production services for EPUB conversion, OCR cleanup, metadata enrichment, ONIX preparation, and publisher backlist recovery.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads publishing and book-production services'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'EPUB conversion and metadata services',
        description: 'HermesReads provides EPUB conversion, ebook formatting, OCR cleanup, metadata enrichment, ONIX preparation, distributor upload preparation, and publisher backlist recovery.',
        provider: organization,
        areaServed: 'Worldwide',
        serviceType: [
          'EPUB conversion',
          'ebook formatting',
          'OCR cleanup',
          'metadata enrichment',
          'ONIX metadata preparation',
          'backlist recovery',
          'catalog cleanup',
          'print-on-demand preparation'
        ],
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'USD'
          },
          url: `${BASE_URL}/services`
        },
        url: `${BASE_URL}/services`
      }
    }
  },
  publishing: {
    en: {
      htmlLang: 'en',
      title: 'HermesReads Publishing | Revived Books, Translations, EPUB Editions, and Print Preparation',
      description: 'HermesReads Publishing prepares selected public-domain works, translations, revived editions, EPUB-first books, and print-on-demand-ready editions with stronger formatting, metadata, classification, and distribution structure.',
      keywords: 'HermesReads Publishing, public domain publishing, translated books, revived books, classic literature editions, EPUB editions, ebook publishing, print on demand books, metadata for books, book classification, forgotten works',
      canonical: `${BASE_URL}/publishing`,
      og: {
        title: 'HermesReads Publishing | Revived Books, Translations, and EPUB Editions',
        description: 'Revived public-domain works, translations, EPUB-first editions, and print-on-demand-ready books prepared with stronger metadata, classification, and distribution structure.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/publishing`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads Publishing editions'
      },
      twitter: {
        title: 'HermesReads Publishing | Revived Books and EPUB Editions',
        description: 'Public-domain revivals, translations, EPUB editions, print-on-demand preparation, and metadata-rich publishing work.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads Publishing editions'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'PublishingOrganization',
        name: 'HermesReads Publishing',
        url: `${BASE_URL}/publishing`,
        parentOrganization: organization,
        description: 'HermesReads Publishing prepares revived public-domain works, translations, EPUB editions, and print-on-demand-ready books with improved formatting, metadata, classification, and distribution structure.'
      }
    }
  },
  categories: {
    en: {
      htmlLang: 'en',
      title: 'Book Categories | Structured Public Domain Library Catalog | HermesReads',
      description: 'Browse our structured public domain catalog by genre, topic, era, or subject. Find philosophy, history, science, literature, and more in our curated metadata collection.',
      keywords: 'book categories, structured catalog, public domain taxonomy, genre browsing, philosophy books, history books, science ebooks, classic literature by category, metadata catalog',
      canonical: `${BASE_URL}/categories`,
      og: {
        title: 'Book Categories | Structured Public Domain Catalog | HermesReads',
        description: 'Explore free public domain books by genre, topic, or era through our organized metadata catalog.',
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads category browsing'
      },
      twitter: {
        title: 'Categories | Structured Catalog | HermesReads',
        description: 'Browse free ebooks by category in our structured metadata catalog.',
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads category page'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Browse Categories',
        url: `${BASE_URL}/categories`,
        inLanguage: 'en',
        publisher: organization
      }
    },
    de: {
      htmlLang: 'de',
      title: 'Kategorien Durchsuchen | HermesReads',
      description: 'Entdecke Kategorien wie Philosophie, Geschichte und Wissenschaft. Kostenlos in unserer Bibliothek.',
      keywords: 'buchkategorien, kostenlose philosophie bücher, gemeinfreie geschichte, wissenschaft ebooks, klassische literatur',
      canonical: `${BASE_URL}/categories`,
      og: {
        title: 'Kategorien Durchsuchen | HermesReads',
        description: 'Entdecke kostenlose gemeinfreie Bücher nach Kategorie.',
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Kategorien | HermesReads',
        description: 'Durchsuche kostenlose E-Books nach Kategorie.',
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Kategorien Durchsuchen',
        url: `${BASE_URL}/categories`,
        inLanguage: 'de',
        publisher: organization
      }
    },
    fr: {
      htmlLang: 'fr',
      title: 'Parcourir les Catégories | HermesReads',
      description: 'Explorez des catégories comme la philosophie, l’histoire et la science. Gratuit dans notre bibliothèque.',
      keywords: 'catégories de livres, livres de philosophie gratuits, histoire domaine public, ebooks scientifiques, littérature classique',
      canonical: `${BASE_URL}/categories`,
      og: {
        title: 'Parcourir les Catégories | HermesReads',
        description: 'Découvrez des ebooks gratuits par catégorie.',
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Catégories | HermesReads',
        description: 'Parcourez des ebooks gratuits par catégorie.',
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Parcourir les Catégories',
        url: `${BASE_URL}/categories`,
        inLanguage: 'fr',
        publisher: organization
      }
    },
    sv: {
      htmlLang: 'sv',
      title: 'Bläddra Kategorier | HermesReads',
      description: 'Utforska kategorier som filosofi, historia och vetenskap. Gratis i vårt bibliotek.',
      keywords: 'bokkategorier, gratis filosofiböcker, public domain historia, vetenskaps e-böcker, klassisk litteratur',
      canonical: `${BASE_URL}/categories`,
      og: {
        title: 'Bläddra Kategorier | HermesReads',
        description: 'Upptäck gratis e-böcker efter kategori.',
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Kategorier | HermesReads',
        description: 'Bläddra gratis e-böcker efter kategori.',
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Bläddra Kategorier',
        url: `${BASE_URL}/categories`,
        inLanguage: 'sv',
        publisher: organization
      }
    },
    es: {
      htmlLang: 'es',
      title: 'Explorar Categorías | HermesReads',
      description: 'Explora categorías como filosofía, historia y ciencia. Gratis en nuestra biblioteca.',
      keywords: 'categorías de libros, libros de filosofía gratuitos, historia dominio público, ebooks científicos, literatura clásica',
      canonical: `${BASE_URL}/categories`,
      og: {
        title: 'Explorar Categorías | HermesReads',
        description: 'Descubre ebooks gratuitos por categoría.',
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Categorías | HermesReads',
        description: 'Explora ebooks gratuitos por categoría.',
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Explorar Categorías',
        url: `${BASE_URL}/categories`,
        inLanguage: 'es',
        publisher: organization
      }
    },
    fi: {
      htmlLang: 'fi',
      title: 'Selaa Kategorioita | HermesReads',
      description: 'Tutustu kategorioihin kuten filosofia, historia ja tiede. Kaikki ilmaiseksi julkisessa kirjastossamme.',
      keywords: 'kirjakategoriat, ilmaiset filosofiakirjat, public domain historia, tieteelliset e-kirjat, klassinen kirjallisuus',
      canonical: `${BASE_URL}/categories`,
      og: {
        title: 'Selaa Kategorioita | HermesReads',
        description: 'Löydä ilmaisia public domain -kirjoja kategorioittain.',
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Kategoriat | HermesReads',
        description: 'Selaa ilmaisia e-kirjoja kategorioittain HermesReads-sivustolla.',
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Selaa Kategorioita',
        url: `${BASE_URL}/categories`,
        inLanguage: 'fi',
        publisher: organization
      }
    },
    pt: {
      htmlLang: 'pt',
      title: 'Explorar Categorias | HermesReads',
      description: 'Explore categorias como filosofia, história e ciência. Tudo grátis em nossa biblioteca de domínio público.',
      keywords: 'categorias de livros, livros de filosofia gratuitos, história de domínio público, ebooks científicos, literatura clássica',
      canonical: `${BASE_URL}/categories`,
      og: {
        title: 'Explorar Categorias | HermesReads',
        description: 'Descubra ebooks gratuitos por categoria.',
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Categorias | HermesReads',
        description: 'Explore ebooks gratuitos por categoria na HermesReads.',
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Explorar Categorias',
        url: `${BASE_URL}/categories`,
        inLanguage: 'pt',
        publisher: organization
      }
    }
  },
  languages: {
    en: {
      htmlLang: 'en',
      title: 'Languages | HermesReads',
      description: 'Access free public domain ebooks in multiple languages. Discover classic translations.',
      keywords: 'ebooks by language, public domain translations, classic literature languages, HermesReads',
      canonical: `${BASE_URL}/languages`,
      og: {
        title: 'Languages | HermesReads',
        description: 'Free ebooks in multiple languages on HermesReads.',
        type: 'website',
        image: '/images/languages-banner.jpg',
        url: `${BASE_URL}/languages`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Languages | HermesReads',
        description: 'Discover free ebooks in various languages.',
        card: 'summary_large_image',
        image: '/images/languages-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Languages',
        url: `${BASE_URL}/languages`,
        inLanguage: 'en',
        publisher: organization
      }
    },
    de: {
      htmlLang: 'de',
      title: 'Sprachen | HermesReads',
      description: 'Greife auf kostenlose gemeinfreie E-Books in mehreren Sprachen zu. Entdecke klassische Übersetzungen.',
      keywords: 'ebooks nach sprache, gemeinfreie übersetzungen, klassische literatur sprachen, HermesReads',
      canonical: `${BASE_URL}/languages`,
      og: {
        title: 'Sprachen | HermesReads',
        description: 'Kostenlose E-Books in verschiedenen Sprachen auf HermesReads.',
        type: 'website',
        image: '/images/languages-banner.jpg',
        url: `${BASE_URL}/languages`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Sprachen | HermesReads',
        description: 'Entdecke kostenlose E-Books in verschiedenen Sprachen.',
        card: 'summary_large_image',
        image: '/images/languages-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Sprachen',
        url: `${BASE_URL}/languages`,
        inLanguage: 'de',
        publisher: organization
      }
    },
    fr: {
      htmlLang: 'fr',
      title: 'Langues | HermesReads',
      description: 'Accédez à des ebooks gratuits du domaine public dans plusieurs langues. Découvrez des traductions classiques.',
      keywords: 'ebooks par langue, traductions domaine public, langues littérature classique, HermesReads',
      canonical: `${BASE_URL}/languages`,
      og: {
        title: 'Langues | HermesReads',
        description: 'Ebooks gratuits dans plusieurs langues sur HermesReads.',
        type: 'website',
        image: '/images/languages-banner.jpg',
        url: `${BASE_URL}/languages`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Langues | HermesReads',
        description: 'Découvrez des ebooks gratuits dans diverses langues.',
        card: 'summary_large_image',
        image: '/images/languages-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Langues',
        url: `${BASE_URL}/languages`,
        inLanguage: 'fr',
        publisher: organization
      }
    },
    sv: {
      htmlLang: 'sv',
      title: 'Språk | HermesReads',
      description: 'Få tillgång till gratis e-böcker i public domain på flera språk. Upptäck klassiska översättningar.',
      keywords: 'e-böcker efter språk, public domain översättningar, klassisk litteratur språk, HermesReads',
      canonical: `${BASE_URL}/languages`,
      og: {
        title: 'Språk | HermesReads',
        description: 'Gratis e-böcker på olika språk på HermesReads.',
        type: 'website',
        image: '/images/languages-banner.jpg',
        url: `${BASE_URL}/languages`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Språk | HermesReads',
        description: 'Upptäck gratis e-böcker på olika språk.',
        card: 'summary_large_image',
        image: '/images/languages-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Språk',
        url: `${BASE_URL}/languages`,
        inLanguage: 'sv',
        publisher: organization
      }
    },
    es: {
      htmlLang: 'es',
      title: 'Idiomas | HermesReads',
      description: 'Accede a ebooks gratuitos de dominio público en múltiples idiomas. Descubre traducciones clásicas.',
      keywords: 'ebooks por idioma, traducciones dominio público, idiomas literatura clásica, HermesReads',
      canonical: `${BASE_URL}/languages`,
      og: {
        title: 'Idiomas | HermesReads',
        description: 'Ebooks gratuitos en varios idiomas en HermesReads.',
        type: 'website',
        image: '/images/languages-banner.jpg',
        url: `${BASE_URL}/languages`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Idiomas | HermesReads',
        description: 'Descubre ebooks gratuitos en varios idiomas.',
        card: 'summary_large_image',
        image: '/images/languages-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Idiomas',
        url: `${BASE_URL}/languages`,
        inLanguage: 'es',
        publisher: organization
      }
    },
    fi: {
      htmlLang: 'fi',
      title: 'Kielet | HermesReads',
      description: 'Pääsy ilmaisiiin public domain -e-kirjoihin useilla kielillä.',
      keywords: 'e-kirjat kielen mukaan, public domain käännökset, klassinen kirjallisuus kielet, HermesReads',
      canonical: `${BASE_URL}/languages`,
      og: {
        title: 'Kielet | HermesReads',
        description: 'Ilmaisia e-kirjoja useilla kielillä.',
        type: 'website',
        image: '/images/languages-banner.jpg',
        url: `${BASE_URL}/languages`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Kielet | HermesReads',
        description: 'Löydä ilmaisia e-kirjoja eri kielillä.',
        card: 'summary_large_image',
        image: '/images/languages-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Kielet',
        url: `${BASE_URL}/languages`,
        inLanguage: 'fi',
        publisher: organization
      }
    },
    pt: {
      htmlLang: 'pt',
      title: 'Idiomas | HermesReads',
      description: 'Acesse e-books de domínio público em vários idiomas.',
      keywords: 'ebooks por idioma, traduções domínio público, literatura clássica idiomas, HermesReads',
      canonical: `${BASE_URL}/languages`,
      og: {
        title: 'Idiomas | HermesReads',
        description: 'E-books grátis em vários idiomas.',
        type: 'website',
        image: '/images/languages-banner.jpg',
        url: `${BASE_URL}/languages`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      twitter: {
        title: 'Idiomas | HermesReads',
        description: 'Descubra e-books grátis em diversos idiomas.',
        card: 'summary_large_image',
        image: '/images/languages-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Idiomas',
        url: `${BASE_URL}/languages`,
        inLanguage: 'pt',
        publisher: organization
      }
    }
  },
  search: {
    en: {
      htmlLang: 'en',
      title: 'Search Public Domain Books | Find Free Classics, Philosophy, History, Science | HermesReads',
      description: 'Search the HermesReads catalog of public-domain books, revived editions, translations, authors, categories, and historical works prepared through structured metadata and publishing workflows.',
      keywords: 'ebook search, public domain search engine, classic books search, find free books, search by author, search by title, full-text search classics, HermesReads search',
      canonical: `${BASE_URL}/search`,
      og: {
        title: 'Search Public Domain Books | HermesReads Library',
        description: 'Find free ebooks by title, author, genre, or language in our public domain catalog.',
        type: 'website',
        image: '/images/homepage-banner.jpg',
        url: `${BASE_URL}/search`,
        site_name: 'HermesReads',
        imageAlt: 'Search HermesReads public domain library'
      },
      twitter: {
        title: 'Search | HermesReads Public Domain Library',
        description: 'Search the HermesReads public domain library of 10,000+ free books.',
        card: 'summary_large_image',
        image: '/images/homepage-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'Search HermesReads'
      },
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Search Public Domain Books',
        url: `${BASE_URL}/search`,
        inLanguage: 'en',
        publisher: organization
      }
    }
  }
};

Object.keys(templates).forEach(p =>
  Object.keys(templates[p]).forEach(l => Object.freeze(templates[p][l]))
);
Object.freeze(templates);

const dynamicTemplates = {
  categories: {
    en: {
      title: ({ value = 'Unknown' }) => `${value} Books | HermesReads`,
      description: ({ value = 'this category' }) => `Browse free public domain ${value.toLowerCase()} books on HermesReads.`,
      keywords: ({ value = 'category' }) => `free ${value.toLowerCase()} books, public domain ${value.toLowerCase()}, ${value.toLowerCase()} classics, HermesReads`,
      og: ({ value = 'Unknown' }) => ({
        title: `${value} Books | HermesReads`,
        description: `Discover free ${value.toLowerCase()} books in our public domain library.`,
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories/${value.toLowerCase()}`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      twitter: ({ value = 'Unknown' }) => ({
        title: `${value} Books | HermesReads`,
        description: `Explore free ${value.toLowerCase()} books on HermesReads.`,
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      structuredData: ({ value = 'Unknown' }) => ({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `${value} Books`,
        'url': `${BASE_URL}/categories/${value.toLowerCase()}`,
        'inLanguage': 'en',
        publisher: organization
      })
    },
    de: {
      title: ({ value = 'Unbekannt' }) => `${value}-Bücher | HermesReads`,
      description: ({ value = 'dieser Kategorie' }) => `Durchsuche kostenlose gemeinfreie ${value.toLowerCase()}-Bücher auf HermesReads.`,
      keywords: ({ value = 'Kategorie' }) => `kostenlose ${value.toLowerCase()} bücher, gemeinfreie ${value.toLowerCase()}, ${value.toLowerCase()} klassiker, HermesReads`,
      og: ({ value = 'Unbekannt' }) => ({
        title: `${value}-Bücher | HermesReads`,
        description: `Entdecke kostenlose ${value.toLowerCase()}-Bücher in unserer Bibliothek.`,
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories/${value.toLowerCase()}`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      twitter: ({ value = 'Unbekannt' }) => ({
        title: `${value}-Bücher | HermesReads`,
        description: `Erkunde kostenlose ${value.toLowerCase()}-Bücher auf HermesReads.`,
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      structuredData: ({ value = 'Unbekannt' }) => ({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `${value}-Bücher`,
        'url': `${BASE_URL}/categories/${value.toLowerCase()}`,
        'inLanguage': 'de',
        publisher: organization
      })
    },
    fr: {
      title: ({ value = 'Inconnu' }) => `Livres ${value} | HermesReads`,
      description: ({ value = 'cette catégorie' }) => `Parcourez des livres gratuits ${value.toLowerCase()} du domaine public sur HermesReads.`,
      keywords: ({ value = 'catégorie' }) => `livres gratuits ${value.toLowerCase()}, domaine public ${value.toLowerCase()}, classiques ${value.toLowerCase()}, HermesReads`,
      og: ({ value = 'Inconnu' }) => ({
        title: `Livres ${value} | HermesReads`,
        description: `Découvrez des livres ${value.toLowerCase()} gratuits dans notre bibliothèque.`,
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories/${value.toLowerCase()}`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      twitter: ({ value = 'Inconnu' }) => ({
        title: `Livres ${value} | HermesReads`,
        description: `Explorez des livres ${value.toLowerCase()} gratuits sur HermesReads.`,
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      structuredData: ({ value = 'Inconnu' }) => ({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `Livres ${value}`,
        'url': `${BASE_URL}/categories/${value.toLowerCase()}`,
        'inLanguage': 'fr',
        publisher: organization
      })
    },
    sv: {
      title: ({ value = 'Okänd' }) => `${value} Böcker | HermesReads`,
      description: ({ value = 'denna kategori' }) => `Bläddra bland gratis public domain ${value.toLowerCase()}-böcker på HermesReads.`,
      keywords: ({ value = 'kategori' }) => `gratis ${value.toLowerCase()} böcker, public domain ${value.toLowerCase()}, ${value.toLowerCase()} klassiker, HermesReads`,
      og: ({ value = 'Okänd' }) => ({
        title: `${value} Böcker | HermesReads`,
        description: `Upptäck gratis ${value.toLowerCase()}-böcker i vårt bibliotek.`,
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories/${value.toLowerCase()}`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      twitter: ({ value = 'Okänd' }) => ({
        title: `${value} Böcker | HermesReads`,
        description: `Utforska gratis ${value.toLowerCase()}-böcker på HermesReads.`,
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      structuredData: ({ value = 'Okänd' }) => ({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `${value} Böcker`,
        'url': `${BASE_URL}/categories/${value.toLowerCase()}`,
        'inLanguage': 'sv',
        publisher: organization
      })
    },
    es: {
      title: ({ value = 'Desconocido' }) => `Libros ${value} | HermesReads`,
      description: ({ value = 'esta categoría' }) => `Explora libros gratuitos ${value.toLowerCase()} de dominio público en HermesReads.`,
      keywords: ({ value = 'categoría' }) => `libros gratuitos ${value.toLowerCase()}, dominio público ${value.toLowerCase()}, clásicos ${value.toLowerCase()}, HermesReads`,
      og: ({ value = 'Desconocido' }) => ({
        title: `Libros ${value} | HermesReads`,
        description: `Descubre libros ${value.toLowerCase()} gratuitos en nuestra biblioteca.`,
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories/${value.toLowerCase()}`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      twitter: ({ value = 'Desconocido' }) => ({
        title: `Libros ${value} | HermesReads`,
        description: `Explora libros ${value.toLowerCase()} gratuitos en HermesReads.`,
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      structuredData: ({ value = 'Desconocido' }) => ({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `Libros ${value}`,
        'url': `${BASE_URL}/categories/${value.toLowerCase()}`,
        'inLanguage': 'es',
        publisher: organization
      })
    },
    fi: {
      title: ({ value = 'Tuntematon' }) => `${value} Kirjat | HermesReads`,
      description: ({ value = 'tässä kategoriassa' }) => `Selaa ilmaisia public domain ${value.toLowerCase()} -kirjoja HermesReads-sivustolla.`,
      keywords: ({ value = 'kategoria' }) => `ilmaiset ${value.toLowerCase()} kirjat, public domain ${value.toLowerCase()}, ${value.toLowerCase()} klassikot, HermesReads`,
      og: ({ value = 'Tuntematon' }) => ({
        title: `${value} Kirjat | HermesReads`,
        description: `Löydä ilmaisia ${value.toLowerCase()} -kirjoja kirjastostamme.`,
        type: 'website',
        image: '/images/categories-banner.jpg',
        url: `${BASE_URL}/categories/${value.toLowerCase()}`,
        site_name: 'HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      twitter: ({ value = 'Tuntematon' }) => ({
        title: `${value} Kirjat | HermesReads`,
        description: `Tutustu ilmaisiin ${value.toLowerCase()} -kirjoihin HermesReads-sivustolla.`,
        card: 'summary_large_image',
        image: '/images/categories-banner.jpg',
        site: '@HermesReads',
        imageAlt: 'HermesReads cover montage'
      }),
      structuredData: ({ value = 'Tuntematon' }) => ({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        'name': `${value} Kirjat`,
        'url': `${BASE_URL}/categories/${value.toLowerCase()}`,
        'inLanguage': 'fi',
        publisher: organization
      })
    }
  }
};

export const getMetadataBundleForStaticPage = (page = 'home', lang = 'en') => {
  const group = templates[page] || {};
  const tpl = group[lang] || group.en || {};

  // Small helpers
  const normalize = (s) => (typeof s === 'string' ? s.trim() : '');
  const softTrim = (s, n) => (s && s.length > n ? s.slice(0, n - 1).trimEnd() + '…' : s);

  // --- Open Graph (defaults + absolutize image/url) ---
  const og = tpl.og ? { ...tpl.og } : {};
  og.image = absolutize(og.image);
  og.url = og.url || `${BASE_URL}/`;
  og.type = og.type || 'website';
  og.title = og.title || tpl.title;
  og.description = og.description || tpl.description;

  // --- Twitter (defaults + absolutize image, add alt fallback) ---
  const twitter = tpl.twitter ? { ...tpl.twitter } : {};
  twitter.card = twitter.card || 'summary_large_image';
  twitter.image = absolutize(twitter.image);
  twitter.imageAlt = twitter.imageAlt || og.imageAlt || tpl.title;

  // --- JSON-LD (defensive absolutize + fill common gaps) ---
  let structuredData = tpl.structuredData ? { ...tpl.structuredData } : {};
  if (structuredData.image) structuredData.image = absolutize(structuredData.image);
  if (structuredData.logo?.url) {
    structuredData.logo = { ...structuredData.logo, url: absolutize(structuredData.logo.url) };
  }
  if (structuredData.publisher?.logo?.url) {
    structuredData.publisher = {
      ...structuredData.publisher,
      logo: { ...structuredData.publisher.logo, url: absolutize(structuredData.publisher.logo.url) }
    };
  }
  if (!structuredData.inLanguage && lang) structuredData.inLanguage = lang;
  if (page === 'home' && !structuredData.potentialAction) {
    structuredData.potentialAction = {
      '@type': 'SearchAction',
      target: `${BASE_URL}/search?query={search_term_string}`,
      'query-input': 'required name=search_term_string'
    };
  }

  // --- Normalize title/description length (gentle, no truncation unless extreme) ---
  const title = softTrim(normalize(tpl.title), 70);
  const description = softTrim(normalize(tpl.description), 160);

  // --- Keywords: always a string (simplifies page usage) ---
  const keywords = Array.isArray(tpl.keywords) ? tpl.keywords.join(', ') : (tpl.keywords || '');

  // --- Robots default (can be overridden in the template) ---
  const robots = tpl.robots || 'index,follow';

  return {
    ...tpl,
    title,
    description,
    keywords,
    robots,
    og: { site_name: 'HermesReads', ...og },
    twitter,
    canonical: tpl.canonical || og.url,
    structuredData
  };
};